/**
 * Visit outcome — what happened to one member's participation in one occurrence,
 * and whether it is charged. Names mirror the vision glossary exactly.
 *
 * This is distinct from the legacy {@link VisitStatus} (Taken/Cancelled/Missed),
 * which mirrors the current backend storage enum. VisitOutcome is the
 * glossary-aligned model used by the (frontend-only, mocked) attendance feature.
 */
export enum VisitOutcome {
  /** No decision recorded yet. */
  Unmarked = "Unmarked",
  /** The member came; counts toward their contract and is charged. */
  Attended = "Attended",
  /** The member came but isn't charged (goodwill, makeup, trial). */
  AttendedComp = "AttendedComp",
  /** Didn't happen for this member, but they're still billed (late cancel, no-show). */
  CancelledCharged = "CancelledCharged",
  /** Didn't happen for this member and they aren't billed. */
  CancelledWaived = "CancelledWaived",
}

/** Which side of the primary Present / Absent toggle an outcome belongs to. */
export type AttendanceSide = "present" | "absent" | "unmarked";

export function outcomeSide(outcome: VisitOutcome): AttendanceSide {
  switch (outcome) {
    case VisitOutcome.Attended:
    case VisitOutcome.AttendedComp:
      return "present";
    case VisitOutcome.CancelledCharged:
    case VisitOutcome.CancelledWaived:
      return "absent";
    default:
      return "unmarked";
  }
}

/** The default outcome when an attendee is moved to a given side via one tap. */
export const DEFAULT_PRESENT_OUTCOME = VisitOutcome.Attended;
export const DEFAULT_ABSENT_OUTCOME = VisitOutcome.CancelledCharged;

/** Outcomes offered in the per-member refine menu, in display order. */
export const REFINABLE_OUTCOMES: VisitOutcome[] = [
  VisitOutcome.Attended,
  VisitOutcome.AttendedComp,
  VisitOutcome.CancelledCharged,
  VisitOutcome.CancelledWaived,
];

/**
 * A short qualifier shown next to the primary side when the outcome differs from
 * the side's default (e.g. "Comp" on the present side, "Waived" on the absent side).
 * Returns null when the outcome is the side's default and needs no qualifier.
 */
export function outcomeQualifier(
  outcome: VisitOutcome
): "comp" | "charged" | "waived" | null {
  switch (outcome) {
    case VisitOutcome.AttendedComp:
      return "comp";
    case VisitOutcome.CancelledCharged:
      return "charged";
    case VisitOutcome.CancelledWaived:
      return "waived";
    default:
      return null;
  }
}
